import { Dispatch, SetStateAction } from "react";
import {
  Radio,
  RadioSize,
  Input,
  Badge,
  BadgeVariant,
  BadgeSize,
  TruncateWithTooltip
} from "shared/components";
import { cx, formatToCurrency } from "shared/utils";
import { Money, Unit } from "shared/types";
import { useAuthContext } from "providers/Auth";

interface SetRentAmountOptionsProps {
  unit: Unit;
  manualRent: RentAmountData | undefined;
  setManualRent: Dispatch<SetStateAction<RentAmountData | undefined>>;
  useListedRent: boolean;
  setUseListedRent: (useListedRent: boolean) => void;
}

export interface RentAmountData {
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
}

export const SetRentAmountOptions = ({
  unit,
  manualRent,
  setManualRent,
  useListedRent,
  setUseListedRent
}: SetRentAmountOptionsProps) => {
  const { permissions, pms } = useAuthContext();

  const handleManualRentChange = (
    field: keyof RentAmountData,
    value: string | undefined
  ) => {
    if (value == "") {
      setManualRent((prev) => ({
        ...prev,
        [field]: undefined
      }));
      return;
    }
    const cents = value ? parseFloat(value) * 100 : 0;
    setManualRent((prev) => ({
      ...prev,
      [field]: { cents, currency: "USD" }
    }));
  };

  return (
    <div className={cx("text-content-700", pms.isManageAmerica && "space-y-3")}>
      <div className="w-full flex items-center">
        <Radio
          id="rent-option-listed"
          checked={useListedRent}
          disabled={!permissions.canEditApplicationRent}
          onChange={() => {
            setUseListedRent(true);
            setManualRent(undefined);
          }}
          label="Use listed rent"
          name="rent-option"
          className="gap-3"
          size={RadioSize.LG}
        />
        {!pms.isManageAmerica ? (
          <Badge
            className="ml-4"
            variant={BadgeVariant.PRIMARY}
            size={BadgeSize.BASE}
          >
            {formatToCurrency({
              cents: unit.monthly_rent_amount?.cents || 0,
              currency: "USD"
            })}
          </Badge>
        ) : (
          <div className="flex-1 flex justify-end items-center gap-4 text-sm text-content-300">
            <div>
              <span className="block">Home rent</span>
              <span className="block font-medium">
                {formatToCurrency({
                  cents: unit.home_rent_amount?.cents || 0,
                  currency: "USD"
                })}
              </span>
            </div>
            <span className="font-medium text-content-1000">+</span>
            <div>
              <span className="block">Site rent</span>
              <span className="block font-medium">
                {formatToCurrency({
                  cents: unit.site_rent_amount?.cents || 0,
                  currency: "USD"
                })}
              </span>
            </div>
            <span className="font-medium text-content-1000">=</span>
            <div>
              <span className="block">Total rent</span>
              <span className="block font-medium text-content-1000 text-right">
                {formatToCurrency({
                  cents: unit.monthly_rent_amount?.cents || 0,
                  currency: "USD"
                })}
              </span>
            </div>
          </div>
        )}
      </div>
      <div
        className={cx(
          "flex",
          pms.isManageAmerica
            ? "flex-col gap-5"
            : "justify-between h-12 items-center"
        )}
      >
        <Radio
          id="rent-option-manual"
          checked={!useListedRent}
          disabled={!permissions.canEditApplicationRent}
          onChange={() => {
            setUseListedRent(false);
            setManualRent(undefined);
          }}
          label="Set rent amount for application"
          name="rent-option"
          className="gap-3"
          size={RadioSize.LG}
        />
        {!useListedRent ? (
          <div className="space-y-4 text-content-1000">
            <div className="flex items-center gap-4 text-sm">
              {pms.isManageAmerica ? (
                <>
                  <div className="flex-1">
                    <Input
                      id="home-rent"
                      label="Home rent"
                      type="money"
                      placeholder={(
                        (unit.home_rent_amount?.cents || 0) / 100
                      ).toString()}
                      value={
                        manualRent?.home_rent_amount?.cents !== undefined
                          ? (manualRent.home_rent_amount.cents / 100).toString()
                          : ""
                      }
                      onChange={(e) =>
                        handleManualRentChange(
                          "home_rent_amount",
                          e.target.value
                        )
                      }
                      min={0}
                      max={99999}
                      required
                      hideErrorMessage
                    />
                  </div>
                  <span className="text-content-1000 font-medium mt-2">+</span>
                  <div className="flex-1">
                    <Input
                      id="site-rent"
                      label="Site rent"
                      type="money"
                      placeholder={(
                        (unit.site_rent_amount?.cents || 0) / 100
                      ).toString()}
                      value={
                        manualRent?.site_rent_amount?.cents !== undefined
                          ? (manualRent.site_rent_amount.cents / 100).toString()
                          : ""
                      }
                      onChange={(e) =>
                        handleManualRentChange(
                          "site_rent_amount",
                          e.target.value
                        )
                      }
                      min={0}
                      max={99999}
                      required
                      hideErrorMessage
                    />
                  </div>
                  <span className="text-content-1000 font-medium mt-2">=</span>
                  <div className="flex flex-col justify-center gap-2 text-right">
                    <span className="text-content-400 min-w-[100px]">
                      Total rent
                    </span>
                    <span className="text-right font-medium">
                      <TruncateWithTooltip
                        content={formatToCurrency({
                          cents:
                            (manualRent?.site_rent_amount?.cents || 0) +
                            (manualRent?.home_rent_amount?.cents || 0),
                          currency: "USD"
                        })}
                        maxWidth="100px"
                        tooltipClassName="z-[9999]"
                      />
                    </span>
                  </div>
                </>
              ) : (
                <Input
                  id="application-rent"
                  type="money"
                  placeholder={(
                    (unit.monthly_rent_amount?.cents || 0) / 100
                  ).toString()}
                  value={
                    manualRent?.rent_amount?.cents !== undefined
                      ? (manualRent.rent_amount.cents / 100).toString()
                      : ""
                  }
                  onChange={(e) =>
                    handleManualRentChange("rent_amount", e.target.value)
                  }
                  min={0}
                  max={99999}
                  className="self-end !w-[126px]"
                  hideErrorMessage
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
